import type { StudioSystemType } from 'types/studio/items'

import { getRoofName } from '../nativeMounting/utils'
import { getVanDerValkPitchedCladdingOptionsFromSystem } from './applyVanDerValkPitchedCladdingOptions'
import {
  buildVanDerValkBuildingOutlineFromSystem,
  type VanDerValkBuildBuildingOutlineErrorReason,
} from './buildVanDerValkBuildingOutline'
import {
  buildVanDerValkFieldsFromSystem,
  buildVanDerValkPitchedFieldsFromSystem,
  type VanDerValkBuildFramesErrorReason,
} from './buildVanDerValkFrames'
import {
  buildVanDerValkPitchedRoofSlopeFromSystem,
  type VanDerValkBuildPitchedRoofSlopeErrorReason,
} from './buildVanDerValkPitchedRoofSlope'
import {
  getVanDerValkBomFrameFromSystem,
  getVanDerValkBomInputIssue,
  getVanDerValkPitchedBomFrameFromSystem,
  getVanDerValkSolarRampBomFieldsFromSystem,
  isVanDerValkSolarRampSelectedOnSystem,
  type VanDerValkBomInputErrorReason,
} from './fetchVanDerValkMountingBom'
import {
  getVanDerValkPitchedRoofStructureIssue,
  type VanDerValkPitchedRoofStructureIssueReason,
} from './getVanDerValkPitchedRoofStructureIssue'
import { getVanDerValkSuitabilityPayload, isVanDerValkSuitabilityReadyForBom } from './getVanDerValkSuitabilityPayload'
import { getVanDerValkPitchedCladdingForRoofType } from './pitchedCladding'
import { isPitchedCladdingOptionsComplete, isVanDerValkPitchedCladdingType } from './pitchedCladdingFieldConfig'

const VAN_DER_VALK_BUILD_PITCHED_ROOF_SLOPE_ERROR_REASONS = new Set<VanDerValkBuildPitchedRoofSlopeErrorReason>([
  'no_roof_slope',
  'multiple_roof_slopes',
  'non_spatial_roof',
  'invalid_roof_slope',
  'roof_slope_too_shallow',
])

export type VanDerValkPitchedCladdingBomIssueReason = 'missing_pitched_cladding_options'
export type VanDerValkPitchedFrameBomIssueReason = 'missing_pitched_frame_fields'

export type VanDerValkMountingTabBomIssue =
  | { kind: 'outline'; reason: VanDerValkBuildBuildingOutlineErrorReason }
  | { kind: 'frames'; reason: VanDerValkBuildFramesErrorReason }
  | { kind: 'inputs'; reason: VanDerValkBomInputErrorReason }
  | { kind: 'cladding'; reason: VanDerValkPitchedCladdingBomIssueReason }
  | { kind: 'frame'; reason: VanDerValkPitchedFrameBomIssueReason }
  | { kind: 'roof_structure'; reason: VanDerValkPitchedRoofStructureIssueReason }
  | { kind: 'roof_slope'; reason: VanDerValkBuildPitchedRoofSlopeErrorReason }

export const VANDERVALK_OUTLINE_ISSUE_I18N: Record<VanDerValkBuildBuildingOutlineErrorReason, string> = {
  no_roof_outline: 'mount_vdv_no_roof_outline',
  multiple_roof_outlines: 'mount_vdv_multiple_roof_outlines',
  non_spatial_roof: 'mount_vdv_non_spatial_roof',
  invalid_roof_outline: 'mount_vdv_invalid_roof_outline',
  outline_too_complex: 'mount_vdv_outline_too_complex',
}

export const VANDERVALK_FRAMES_ISSUE_I18N: Record<VanDerValkBuildFramesErrorReason, string> = {
  no_active_layout: 'mount_vdv_frames_no_active_layout',
  matrix_too_large: 'mount_vdv_frames_matrix_too_large',
  ew_requires_dual_tilt: 'mount_vdv_frames_ew_requires_dual_tilt',
  invalid_field_location: 'mount_vdv_invalid_field_location',
}

export const VANDERVALK_INPUTS_ISSUE_I18N: Record<VanDerValkBomInputErrorReason, string> = {
  missing_wind_speed: 'mount_vdv_missing_wind_speed',
  missing_altitude: 'mount_vdv_missing_altitude',
  missing_topography_slope: 'mount_vdv_missing_topography_slope',
  missing_topography_zone: 'mount_vdv_missing_topography_zone',
  missing_solar_ramp_anchor_fields: 'mount_vdv_missing_solar_ramp_anchor_fields',
}

export const VANDERVALK_CLADDING_ISSUE_I18N: Record<VanDerValkPitchedCladdingBomIssueReason, string> = {
  missing_pitched_cladding_options: 'mount_vdv_missing_pitched_cladding_options',
}

export const VANDERVALK_FRAME_ISSUE_I18N: Record<VanDerValkPitchedFrameBomIssueReason, string> = {
  missing_pitched_frame_fields: 'mount_vdv_missing_pitched_frame_fields',
}

export const VANDERVALK_ROOF_STRUCTURE_ISSUE_I18N: Record<VanDerValkPitchedRoofStructureIssueReason, string> = {
  no_roof_facet: 'mount_vdv_pitched_quick_roof_required',
  unsupported_manual_roof: 'mount_vdv_pitched_quick_roof_required',
  unsupported_roof_structure: 'mount_vdv_unsupported_roof_structure',
}

export const VANDERVALK_ROOF_SLOPE_ISSUE_I18N: Record<VanDerValkBuildPitchedRoofSlopeErrorReason, string> = {
  no_roof_slope: 'mount_vdv_no_roof_outline',
  multiple_roof_slopes: 'mount_vdv_multiple_roof_outlines',
  non_spatial_roof: 'mount_vdv_non_spatial_roof',
  invalid_roof_slope: 'mount_vdv_invalid_roof_outline',
  roof_slope_too_shallow: 'mount_vdv_roof_slope_too_shallow',
}

/**
 * Returns null when panel specs are invalid (handled separately on the mounting tab) or when
 * the user has not yet selected a suitable system (cached specs must match current modules).
 */
export function getVanDerValkMountingTabBomIssue(
  system: StudioSystemType | null | undefined
): VanDerValkMountingTabBomIssue | null {
  const pre = getVanDerValkSuitabilityPayload(system)
  if (!pre.ok) {
    return null
  }

  const isPitched = system?.mounting_type === 'onRoof'

  if (isPitched) {
    const roofStructureIssue = getVanDerValkPitchedRoofStructureIssue(system)
    if (roofStructureIssue) {
      return { kind: 'roof_structure', reason: roofStructureIssue }
    }
    const roofSlopeBuilt = buildVanDerValkPitchedRoofSlopeFromSystem(system)
    if (!roofSlopeBuilt.ok) {
      return { kind: 'roof_slope', reason: roofSlopeBuilt.reason }
    }
  }

  if (!isVanDerValkSuitabilityReadyForBom(system)) {
    return null
  }

  const inputIssue = getVanDerValkBomInputIssue(system)
  if (inputIssue) {
    return { kind: 'inputs', reason: inputIssue }
  }

  if (isPitched) {
    const roofTypeUrl = window.projectForm?.getState?.()?.values?.roof_type
    const roofTypeName = getRoofName(roofTypeUrl)
    const claddingType = getVanDerValkPitchedCladdingForRoofType(roofTypeName)
    if (claddingType && isVanDerValkPitchedCladdingType(claddingType)) {
      const options = getVanDerValkPitchedCladdingOptionsFromSystem(system)
      if (!isPitchedCladdingOptionsComplete(claddingType, options)) {
        return { kind: 'cladding', reason: 'missing_pitched_cladding_options' }
      }
      const pitchedFrame = getVanDerValkPitchedBomFrameFromSystem(system, claddingType)
      if (!pitchedFrame) {
        return { kind: 'frame', reason: 'missing_pitched_frame_fields' }
      }
    } else {
      const pitchedFrame = getVanDerValkPitchedBomFrameFromSystem(system)
      if (!pitchedFrame) {
        return null
      }
    }
    const fieldsBuilt = buildVanDerValkPitchedFieldsFromSystem(system)
    if (!fieldsBuilt.ok) {
      if (VAN_DER_VALK_BUILD_PITCHED_ROOF_SLOPE_ERROR_REASONS.has(fieldsBuilt.reason as VanDerValkBuildPitchedRoofSlopeErrorReason)) {
        return { kind: 'roof_slope', reason: fieldsBuilt.reason as VanDerValkBuildPitchedRoofSlopeErrorReason }
      }
      return { kind: 'frames', reason: fieldsBuilt.reason as VanDerValkBuildFramesErrorReason }
    }
    return null
  }

  const outlineBuilt = buildVanDerValkBuildingOutlineFromSystem(system)
  if (!outlineBuilt.ok) {
    return { kind: 'outline', reason: outlineBuilt.reason }
  }

  const bomFrame = getVanDerValkBomFrameFromSystem(system)
  if (!bomFrame) {
    return null
  }

  const solarRamp = isVanDerValkSolarRampSelectedOnSystem(system)
  if (solarRamp && !getVanDerValkSolarRampBomFieldsFromSystem(system)) {
    return { kind: 'inputs', reason: 'missing_solar_ramp_anchor_fields' }
  }

  const fieldsBuilt = buildVanDerValkFieldsFromSystem(system, bomFrame, { solarRamp })
  if (!fieldsBuilt.ok) {
    return { kind: 'frames', reason: fieldsBuilt.reason }
  }

  return null
}

export function vanDerValkMountingTabBomIssueI18nKey(issue: VanDerValkMountingTabBomIssue): string {
  if (issue.kind === 'outline') {
    return VANDERVALK_OUTLINE_ISSUE_I18N[issue.reason]
  }
  if (issue.kind === 'inputs') {
    return VANDERVALK_INPUTS_ISSUE_I18N[issue.reason]
  }
  if (issue.kind === 'cladding') {
    return VANDERVALK_CLADDING_ISSUE_I18N[issue.reason]
  }
  if (issue.kind === 'frame') {
    return VANDERVALK_FRAME_ISSUE_I18N[issue.reason]
  }
  if (issue.kind === 'roof_structure') {
    return VANDERVALK_ROOF_STRUCTURE_ISSUE_I18N[issue.reason]
  }
  if (issue.kind === 'roof_slope') {
    return VANDERVALK_ROOF_SLOPE_ISSUE_I18N[issue.reason]
  }
  return VANDERVALK_FRAMES_ISSUE_I18N[issue.reason]
}
